/**
 * Rules for loading and persisting the map's full recall dataset.
 *
 * Kept pure so the paging and cache-write decisions can be tested without the hook.
 */

/**
 * PostgREST caps every response, RPC results included, at `db-max-rows`
 * (`max_rows = 1000` in vehixdb-prod `supabase/config.toml`). A page shorter
 * than the requested size is therefore not proof of the last page: a capped
 * page is exactly this long.
 */
export const SERVER_MAX_ROWS = 1000;

/** Persisting a set this much smaller than the stored copy needs a second, confirming load. */
export const SHRINK_GUARD_RATIO = 0.7;

export interface RawRecallsMeta {
  /** True only when every page was loaded. */
  complete: boolean;
  /** Row count of the stored copy this meta describes. */
  count: number;
  /** A smaller load seen once and not yet persisted; the next smaller load confirms it. */
  pendingShrinkCount?: number;
}

/** True when a page of `rowsReturned` rows ends the dataset. */
export function isLastPage(rowsReturned: number, pageSize: number): boolean {
  return rowsReturned < Math.min(pageSize, SERVER_MAX_ROWS);
}

export interface LoadAllPagesOptions<R> {
  /** Rows requested per page; full pages must come back exactly this long. */
  pageSize: number;
  maxPages: number;
  /** Pages requested at once after the first. */
  concurrency: number;
  /** Called with the first page when more pages follow, so it can be shown early. */
  onFirstPage?: (rows: R[]) => void;
  isCancelled?: () => boolean;
}

export interface LoadedPages<R> {
  rows: R[];
  /** True when a short page ended the dataset (not stopped by maxPages or cancellation). */
  complete: boolean;
  requests: number;
}

/**
 * Load every page: the first alone, then the rest `concurrency` at a time at fixed
 * offsets. Rows come back in page order. `fetchPage` throws on failure.
 */
export async function loadAllPages<R>(
  fetchPage: (offset: number, limit: number) => Promise<R[]>,
  { pageSize, maxPages, concurrency, onFirstPage, isCancelled }: LoadAllPagesOptions<R>
): Promise<LoadedPages<R>> {
  const pages: R[][] = [await fetchPage(0, pageSize)];
  let requests = 1;
  const done = (complete: boolean) => ({ rows: pages.flat(), complete, requests });

  if (isLastPage(pages[0]?.length ?? 0, pageSize)) {
    return done(true);
  }
  if (isCancelled?.()) {
    return done(false);
  }
  onFirstPage?.(pages[0] ?? []);

  for (let next = 1; next < maxPages; next += concurrency) {
    const wave = Array.from(
      { length: Math.min(concurrency, maxPages - next) },
      (_, i) => (next + i) * pageSize
    );
    const results = await Promise.all(wave.map(offset => fetchPage(offset, pageSize)));
    requests += wave.length;
    if (isCancelled?.()) {
      return done(false);
    }
    for (const rows of results) {
      pages.push(rows);
      if (isLastPage(rows.length, pageSize)) {
        return done(true);
      }
    }
  }
  return done(false);
}

export interface StoredDataset {
  count: number;
  meta: RawRecallsMeta | null;
}

export interface RawCacheWriteDecision {
  /** Persist the freshly loaded rows. */
  writeRows: boolean;
  /** Meta to persist, or null to leave the stored meta alone. */
  meta: RawRecallsMeta | null;
}

/**
 * Decide what to persist after a network load.
 *
 * - An incomplete load (page cap hit) is never persisted.
 * - A load much smaller than the stored copy is held back once and recorded as
 *   pending; a second smaller load confirms the drop and is persisted. The
 *   screen always shows the server's answer — this only protects the copy the
 *   next visit paints first.
 */
export function decideRawCacheWrite(
  loadedCount: number,
  complete: boolean,
  stored: StoredDataset | null
): RawCacheWriteDecision {
  if (!complete || loadedCount === 0) {
    return { writeRows: false, meta: null };
  }

  const fresh: RawRecallsMeta = { complete: true, count: loadedCount };
  if (!stored || stored.count === 0) {
    return { writeRows: true, meta: fresh };
  }

  const shrinks = loadedCount < stored.count * SHRINK_GUARD_RATIO;
  if (!shrinks) {
    return { writeRows: true, meta: fresh };
  }

  const confirmed = stored.meta?.pendingShrinkCount !== undefined;
  if (confirmed) {
    return { writeRows: true, meta: fresh };
  }

  return {
    writeRows: false,
    meta: {
      complete: stored.meta?.complete ?? false,
      count: stored.count,
      pendingShrinkCount: loadedCount
    }
  };
}

/** Whether a stored copy can be trusted as the complete dataset. */
export function isStoredDatasetComplete(count: number, meta: RawRecallsMeta | null): boolean {
  return meta?.complete === true && meta.count === count;
}
